// Show analytics: headline tiles, daily downloads, top episodes, apps.
// One series throughout, so one hue (the host's accent) and no legend;
// values stay in text tokens; every value is also in the table view.

import { useState } from 'react';
import { compactNumber, niceTicks, useShowAnalytics, type ShowAnalytics } from '../../lib/shows';
import { ErrorMessage, Loading } from '../ui/Feedback';

const RANGES = [7, 30, 90] as const;

export function Analytics({ podcastId }: { podcastId: string }) {
  const [days, setDays] = useState<number>(30);
  const [asTable, setAsTable] = useState(false);
  const analytics = useShowAnalytics(podcastId, days);

  return (
    <section aria-labelledby="analytics-heading" className="space-y-5 rounded-xl border border-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="analytics-heading" className="text-lg font-semibold">
          Downloads
        </h2>
        <div className="flex items-center gap-3">
          <div role="group" aria-label="Time range" className="inline-flex rounded-md border border-border p-0.5">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={days === r}
                onClick={() => setDays(r)}
                className={`rounded px-2.5 py-1 text-xs ${days === r ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:text-text'}`}
              >
                {r} days
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setAsTable((t) => !t)} className="text-xs text-muted underline hover:text-text">
            {asTable ? 'Show charts' : 'Show as table'}
          </button>
        </div>
      </div>

      {analytics.isLoading && <Loading />}
      {analytics.isError && <ErrorMessage error={analytics.error} onRetry={() => analytics.refetch()} />}
      {analytics.data && <AnalyticsBody data={analytics.data} asTable={asTable} />}
    </section>
  );
}

function AnalyticsBody({ data, asTable }: { data: ShowAnalytics; asTable: boolean }) {
  if (data.totals.allTime === 0) {
    return (
      <p className="text-sm text-muted">
        No downloads yet. Once your feed is in Apple Podcasts or Spotify, every listener’s download shows up here within
        seconds — counted once per listener per day, bots excluded.
      </p>
    );
  }
  const avg = data.totals.window / data.days;
  return (
    <div className="space-y-6">
      <dl className="grid gap-4 sm:grid-cols-3">
        <Tile label={`Last ${data.days} days`} value={compactNumber(data.totals.window)} hero />
        <Tile label="All time" value={compactNumber(data.totals.allTime)} />
        <Tile label="Average per day" value={avg >= 10 ? compactNumber(Math.round(avg)) : avg.toFixed(1)} />
      </dl>
      {asTable ? <Tables data={data} /> : <Charts data={data} />}
      <p className="text-xs text-muted">Counted once per listener per episode per day; bots and partial requests excluded.</p>
    </div>
  );
}

function Tile({ label, value, hero }: { label: string; value: string; hero?: boolean }) {
  return (
    <div className="rounded-lg bg-surface p-4">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={`mt-1 font-semibold text-text ${hero ? 'text-4xl' : 'text-2xl'}`}>{value}</dd>
    </div>
  );
}

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function Charts({ data }: { data: ShowAnalytics }) {
  return (
    <div className="space-y-6">
      <DailyColumns daily={data.daily} />
      <div className="grid gap-6 lg:grid-cols-2">
        <RankedBars
          title="Top episodes"
          rows={data.episodes.filter((e) => e.window > 0).slice(0, 8).map((e) => ({ label: e.title, value: e.window }))}
          empty="No episode downloads in this range."
        />
        <RankedBars title="Listening apps" rows={data.apps.map((a) => ({ label: a.name, value: a.downloads }))} empty="No app data in this range." />
      </div>
    </div>
  );
}

export function DailyColumns({ daily }: { daily: ShowAnalytics['daily'] }) {
  const [active, setActive] = useState<number | null>(null);
  const ticks = niceTicks(Math.max(...daily.map((d) => d.downloads), 0));
  const top = ticks[ticks.length - 1]!;
  const hovered = active !== null ? daily[active] : null;

  return (
    <figure>
      <figcaption className="mb-2 text-sm font-medium text-text">Daily downloads</figcaption>
      <div className="flex gap-2">
        <div aria-hidden className="relative h-40 w-8 shrink-0 text-right text-[10px] text-muted">
          {ticks.map((t) => (
            <span key={t} className="absolute right-0 -translate-y-1/2" style={{ bottom: `calc(${(t / top) * 100}% - 0.5em)` }}>
              {t.toLocaleString()}
            </span>
          ))}
        </div>
        <div className="relative h-40 flex-1" onPointerLeave={() => setActive(null)}>
          {ticks.map((t) => (
            <div key={t} aria-hidden className="absolute inset-x-0 border-t border-border" style={{ bottom: `${(t / top) * 100}%` }} />
          ))}
          <div className="absolute inset-0 flex items-end justify-center gap-[2px]">
            {daily.map((d, i) => (
              <button
                key={d.date}
                type="button"
                aria-label={`${fmtDate(d.date)}: ${d.downloads} downloads`}
                onPointerEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                className="flex h-full max-w-6 flex-1 items-end focus:outline-none"
              >
                <span
                  className={`block w-full rounded-t-[4px] ${active === i ? 'bg-accent-hover' : 'bg-accent'}`}
                  style={{ height: d.downloads ? `${Math.max((d.downloads / top) * 100, 1.5)}%` : 0 }}
                />
              </button>
            ))}
          </div>
          {hovered && (
            <div
              role="tooltip"
              className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-bg px-2 py-1 text-xs shadow"
              style={{ left: `${((active! + 0.5) / daily.length) * 100}%` }}
            >
              <strong className="text-text">{hovered.downloads.toLocaleString()}</strong>{' '}
              <span className="text-muted">{fmtDate(hovered.date)}</span>
            </div>
          )}
        </div>
      </div>
      <div aria-hidden className="ml-10 mt-1 flex justify-between text-[10px] text-muted">
        <span>{fmtDate(daily[0]!.date)}</span>
        <span>{fmtDate(daily[daily.length - 1]!.date)}</span>
      </div>
    </figure>
  );
}

export function RankedBars({ title, rows, empty }: { title: string; rows: Array<{ label: string; value: number }>; empty: string }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <figure>
      <figcaption className="mb-2 text-sm font-medium text-text">{title}</figcaption>
      {rows.length === 0 ? (
        <p className="text-xs text-muted">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.label} className="grid grid-cols-[minmax(0,10rem)_1fr] items-center gap-3 text-xs">
              <span className="truncate text-muted" title={r.label}>
                {r.label}
              </span>
              <span className="flex items-center gap-2">
                <span className="block h-4 rounded-r-[4px] bg-accent" style={{ width: `${(r.value / max) * 85}%`, minWidth: 2 }} />
                <span className="font-medium text-text">{r.value.toLocaleString()}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}

function Tables({ data }: { data: ShowAnalytics }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <table className="w-full text-left text-xs">
        <caption className="mb-2 text-left text-sm font-medium">Daily downloads</caption>
        <thead className="text-muted">
          <tr>
            <th className="py-1 font-medium">Date</th>
            <th className="py-1 text-right font-medium">Downloads</th>
          </tr>
        </thead>
        <tbody>
          {data.daily.map((d) => (
            <tr key={d.date} className="border-t border-border">
              <td className="py-1">{fmtDate(d.date)}</td>
              <td className="py-1 text-right tabular-nums">{d.downloads.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="space-y-6">
        <table className="w-full text-left text-xs">
          <caption className="mb-2 text-left text-sm font-medium">Episodes</caption>
          <thead className="text-muted">
            <tr>
              <th className="py-1 font-medium">Episode</th>
              <th className="py-1 text-right font-medium">{data.days} days</th>
              <th className="py-1 text-right font-medium">All time</th>
            </tr>
          </thead>
          <tbody>
            {data.episodes.map((e) => (
              <tr key={e.episodeId} className="border-t border-border">
                <td className="py-1">{e.title}</td>
                <td className="py-1 text-right tabular-nums">{e.window.toLocaleString()}</td>
                <td className="py-1 text-right tabular-nums">{e.allTime.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="w-full text-left text-xs">
          <caption className="mb-2 text-left text-sm font-medium">Apps</caption>
          <tbody>
            {data.apps.map((a) => (
              <tr key={a.name} className="border-t border-border">
                <td className="py-1">{a.name}</td>
                <td className="py-1 text-right tabular-nums">{a.downloads.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
